import type { GraphQLError } from 'graphql';

import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';

import useIsUserViewingSpaceAsAnonQuery from './__generated__/useIsUserViewingSpaceAsAnonQuery.graphql.ts';
import type {
	useIsUserViewingSpaceAsAnonQuery$data as Q,
	useIsUserViewingSpaceAsAnonQuery$variables as V,
} from './__generated__/useIsUserViewingSpaceAsAnonQuery.graphql.ts';

export async function preloadIsUserViewingSpaceAsAnon(spaceKey: string): Promise<void> {
	const result = await query<Q, V>({
		query: useIsUserViewingSpaceAsAnonQuery,
		variables: { spaceKey },
		errorPolicy: 'all',
		fetchPolicy: 'network-only',
	});

	if (result?.errors) {
		result.errors.forEach((error: GraphQLError) => {
			markErrorAsHandled(error);
		});
	}

	// When a space is restricted, the query returns a 403 PermissionException with
	// `data.space === null`. Apollo's error handler evicts this from the cache, so by the
	// time the React tree renders during SSR the cache entry is gone and `useQueryInterop`
	// returns `loading: true`. On the client, however, the same query resolves with
	// `loading: false` — causing a hydration mismatch in the SideNav footer.
	//
	// To bridge this gap we persist the restricted-space signal in a global that
	// `useIsUserViewingSpaceAsAnon` can read synchronously during both SSR and hydration,
	// bypassing the evicted Apollo cache.
	if (result?.errors?.length && result?.data?.space === null) {
		const target = typeof window !== 'undefined' ? window : (globalThis as any);
		target['__RESTRICTED_SPACES_FROM_PRELOAD__'] =
			target['__RESTRICTED_SPACES_FROM_PRELOAD__'] || {};
		target['__RESTRICTED_SPACES_FROM_PRELOAD__'][spaceKey] = true;
	}
}
